import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { profileAPI, creatorAPI, reportAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LoadingAnimation from '../components/LoadingAnimation';
import './CreatorProfile.css';

const CreatorProfile = () => {
  const { userId } = useParams();
  const { userId: currentUserId } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('timeline');
  const [comments, setComments] = useState({});
  const [commentText, setCommentText] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [statusText, setStatusText] = useState('');
  const [postingStatus, setPostingStatus] = useState(false);

  const isOwner = userId === currentUserId?.toString();

  useEffect(() => {
    loadData();
  }, [userId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const profileRes = await profileAPI.getUserProfile(userId);
      setProfile(profileRes.data);

      if (!profileRes.data.isCreator) {
        navigate(`/profile/${userId}`);
        return;
      }

      const subscriptionRes = await creatorAPI.isSubscribed(userId);
      setIsSubscribed(subscriptionRes.data.isSubscribed);
      
      // Load content if subscribed or owner
      if (subscriptionRes.data.isSubscribed || isOwner) {
        try {
          const contentRes = await creatorAPI.getCreatorContent(userId);
          const contentData = contentRes.data || [];
          setContent(contentData);
          
          // Load comments for all content
          const commentsData = {};
          for (const item of contentData) {
            try {
              const commentsRes = await creatorAPI.getComments(item.id);
              commentsData[item.id] = commentsRes.data || [];
            } catch (err) {
              commentsData[item.id] = [];
            }
          }
          setComments(commentsData);
        } catch (err) {
          console.error('Failed to load content:', err);
        }
      }
    } catch (err) {
      console.error('Failed to load creator profile:', err);
      setError('Failed to load creator profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    // PHASE 1: Creator subscriptions disabled - all content is free
    setError('Creator subscriptions are not available in Phase 1. All content is free!');
    setTimeout(() => setError(''), 3000);
  };

  const handleLikeContent = async (contentId) => {
    try {
      const response = await creatorAPI.toggleLike(contentId);
      setContent(prevContent => 
        prevContent.map(item => {
          if (item.id === contentId) {
            return {
              ...item,
              likesCount: response.data.liked 
                ? (item.likesCount || 0) + 1 
                : Math.max((item.likesCount || 0) - 1, 0)
            };
          }
          return item;
        })
      );
    } catch (err) {
      console.error('Failed to like content:', err);
    }
  };

  const handleToggleComments = (contentId) => {
    setExpandedComments(prev => ({
      ...prev,
      [contentId]: !prev[contentId]
    }));
  };

  const handleAddComment = async (contentId) => {
    const text = commentText[contentId]?.trim();
    if (!text) return;
    
    try {
      const response = await creatorAPI.addComment(contentId, text);
      setComments(prev => ({
        ...prev,
        [contentId]: [response.data.comment, ...(prev[contentId] || [])]
      }));
      setContent(prevContent =>
        prevContent.map(item => {
          if (item.id === contentId) {
            return {
              ...item,
              commentsCount: (item.commentsCount || 0) + 1
            };
          }
          return item;
        })
      );
      setCommentText(prev => ({ ...prev, [contentId]: '' }));
    } catch (err) {
      console.error('Failed to add comment:', err);
    }
  };

  const handleDeleteComment = async (contentId, commentId) => {
    try {
      await creatorAPI.deleteComment(commentId);
      setComments(prev => ({
        ...prev,
        [contentId]: (prev[contentId] || []).filter(c => c.id !== commentId)
      }));
      setContent(prevContent =>
        prevContent.map(item => {
          if (item.id === contentId) {
            return {
              ...item,
              commentsCount: Math.max(0, (item.commentsCount || 0) - 1)
            };
          }
          return item;
        })
      );
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const handlePostStatus = async () => {
    const text = statusText.trim();
    if (!text || postingStatus) return;

    setPostingStatus(true);
    try {
      await creatorAPI.uploadContent('POST', null, null, text, false);
      setStatusText('');
      setSuccess('Status posted successfully!');
      setTimeout(() => setSuccess(''), 3000);
      await loadData();
    } catch (err) {
      console.error('Failed to post status:', err);
      setError('Failed to post status');
      setTimeout(() => setError(''), 3000);
    } finally {
      setPostingStatus(false);
    }
  };

  const handleSendMessage = () => {
    navigate(`/inbox`);
  };

  const handleReport = async () => {
    const reasons = [
      { value: 'HARASSMENT', label: 'Harassment or bullying' },
      { value: 'FAKE_PROFILE', label: 'Fake profile or impersonation' },
      { value: 'INAPPROPRIATE_CONTENT', label: 'Inappropriate content' },
      { value: 'SPAM', label: 'Spam or misleading' },
      { value: 'SCAM', label: 'Scam or fraud' },
      { value: 'OTHER', label: 'Other' }
    ];
    
    const reasonText = reasons.map((r, i) => `${i + 1}. ${r.label}`).join('\n');
    const choice = prompt(`Report ${profile.name}?\n\nSelect reason (1-6):\n${reasonText}`);
    
    if (!choice) return;
    
    const index = parseInt(choice) - 1;
    if (index < 0 || index >= reasons.length) {
      alert('Invalid selection');
      return;
    }
    
    const reason = reasons[index].value;
    const description = prompt('Please provide additional details (optional):');
    
    try {
      await reportAPI.reportUser(userId, reason, description || '');
      alert('Thank you for your report. Our team will review it within 24 hours.');
    } catch (err) {
      console.error('Failed to submit report:', err);
      alert(err.response?.data?.error || 'Failed to submit report. You may have already reported this user.');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <LoadingAnimation />
      </div>
    );
  }

  if (!profile) {
    return <div>Profile not found</div>;
  }

  const subscriberCount = profile.subscriberCount || 0;
  const postCount = content.length;

  return (
    <div className="creator-profile-container">
      {/* Cover Photo */}
      <div className="profile-cover">
        {profile.coverPhotoUrl ? (
          <img src={profile.coverPhotoUrl} alt="Cover" />
        ) : (
          <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', width: '100%', height: '100%' }} />
        )}
      </div>

      {/* Profile Header */}
      <div className="profile-header">
        <div className="profile-header-content">
          <div className="profile-avatar">
            {profile.photoUrls && profile.photoUrls.length > 0 ? (
              <img src={profile.photoUrls[0]} alt={profile.name} />
            ) : (
              <div style={{ width: '100%', height: '100%', background: '#e4e6eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px', fontWeight: '700', color: '#65676b' }}>
                {profile.name?.charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          <div className="profile-info">
            <div className="profile-name">
              <h1>{profile.name}</h1>
              {profile.hasVerifiedBadge && (
                <div className="verified-badge">✓</div>
              )}
            </div>
            
            {/* Feature Badges */}
            {(profile.availableForTravel || profile.hasParking) && (
              <div className="feature-badges">
                {profile.availableForTravel && (
                  <div className="feature-badge travel">
                    <span className="badge-icon">✈️</span>
                    <span>Available to travel</span>
                  </div>
                )}
                {profile.hasParking && (
                  <div className="feature-badge parking">
                    <span className="badge-icon">🅿️</span>
                    <span>Parking available</span>
                  </div>
                )}
              </div>
            )}
            
            <div className="profile-stats">
              <span className="profile-stat">{postCount} posts</span>
              <span className="profile-stat">{subscriberCount} subscribers</span>
            </div>
          </div>

          {!isOwner && (
            <div className="profile-actions">
              {isSubscribed ? (
                <>
                  <button className="btn-primary btn-icon" onClick={handleSendMessage}>
                    <span>💬</span>
                    <span>Message</span>
                  </button>
                  <button className="btn-secondary">Subscribed ✓</button>
                  <button className="btn-report" onClick={handleReport} title="Report user">
                    <span>⚠️</span>
                  </button>
                </>
              ) : (
                <>
                  <button className="btn-primary btn-icon" onClick={handleSubscribe} disabled={subscribing}>
                    <span>💎</span>
                    <span>{subscribing ? 'Subscribing...' : `Subscribe • ${profile.subscriptionPrice} credits`}</span>
                  </button>
                  <button className="btn-secondary btn-icon" onClick={handleSendMessage}>
                    <span>💬</span>
                    <span>Message</span>
                  </button>
                  <button className="btn-report" onClick={handleReport} title="Report user">
                    <span>⚠️</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="profile-nav">
        <button 
          className={`nav-tab ${activeTab === 'timeline' ? 'active' : ''}`}
          onClick={() => setActiveTab('timeline')}
        >
          Timeline
        </button>
        <button 
          className={`nav-tab ${activeTab === 'about' ? 'active' : ''}`}
          onClick={() => setActiveTab('about')}
        >
          About
        </button>
        <button 
          className={`nav-tab ${activeTab === 'photos' ? 'active' : ''}`}
          onClick={() => setActiveTab('photos')}
        >
          Photos
        </button>
      </div>

      {/* Main Content */}
      <div className="profile-content">
        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <>
            {/* Left Sidebar */}
            <div className="profile-sidebar">
              {/* About Card */}
              <div className="sidebar-card">
                <h3>About</h3>
                <p>{profile.creatorBio || profile.bio || 'No bio available'}</p>
              </div>

              {/* Info Card */}
              <div className="sidebar-card">
                <h3>Info</h3>
                <div className="info-item">
                  <span className="info-icon">📍</span>
                  <span>{profile.location || 'Location not set'}</span>
                </div>
                {profile.age && (
                  <div className="info-item">
                    <span className="info-icon">🎂</span>
                    <span>{profile.age} years old</span>
                  </div>
                )}
                {profile.availableForTravel && (
                  <div className="info-item">
                    <span className="info-icon">✈️</span>
                    <span>Available to travel</span>
                  </div>
                )}
                {profile.hasParking && (
                  <div className="info-item">
                    <span className="info-icon">🅿️</span>
                    <span>Parking available</span>
                  </div>
                )}
              </div>

              {/* Subscription Info */}
              {!isOwner && !isSubscribed && (
                <div className="subscription-banner">
                  <h3>💎 Exclusive Content</h3>
                  <p>Subscribe to access exclusive photos and videos</p>
                  <div className="subscription-price">{profile.subscriptionPrice} credits/month</div>
                  <button className="btn-primary" onClick={handleSubscribe} disabled={subscribing} style={{ width: '100%' }}>
                    {subscribing ? 'Subscribing...' : 'Subscribe Now'}
                  </button>
                </div>
              )}
            </div>

            {/* Main Feed */}
            <div className="profile-feed">
              {error && (
                <div style={{ background: '#fee', color: '#c00', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
                  {error}
                </div>
              )}
              {success && (
                <div style={{ background: '#efe', color: '#060', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
                  {success}
                </div>
              )}

              {/* Status Input Box - Only for Owner */}
              {isOwner && (
                <div className="status-input-card">
                  <div className="status-input-header">
                    <div className="status-avatar">
                      {profile.photoUrls && profile.photoUrls.length > 0 ? (
                        <img src={profile.photoUrls[0]} alt={profile.name} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', background: '#e4e6eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: '700', color: '#65676b' }}>
                          {profile.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <textarea
                      className="status-input"
                      placeholder="What's on your mind?"
                      value={statusText}
                      onChange={(e) => setStatusText(e.target.value)}
                      rows="3"
                    />
                  </div>
                  <div className="status-input-footer">
                    <button 
                      className="btn-post-status"
                      onClick={handlePostStatus}
                      disabled={!statusText.trim() || postingStatus}
                    >
                      {postingStatus ? 'Posting...' : 'Post'}
                    </button>
                  </div>
                </div>
              )}

              {!isSubscribed && !isOwner ? (
                <div className="empty-state">
                  <div className="empty-state-icon">🔒</div>
                  <h3>Subscribe to see posts</h3>
                  <p>Subscribe to {profile.name} to access exclusive content</p>
                </div>
              ) : content.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">📝</div>
                  <h3>No posts yet</h3>
                  <p>Check back later for new content</p>
                </div>
              ) : (
                content.map((post) => (
                  <div key={post.id} className="post-card">
                    {/* Post content - keeping existing post rendering */}
                    <div className="post-header">
                      <div className="post-avatar">
                        {profile.photoUrls && profile.photoUrls.length > 0 ? (
                          <img src={profile.photoUrls[0]} alt={profile.name} />
                        ) : (
                          <div style={{ width: '100%', height: '100%', background: '#e4e6eb' }} />
                        )}
                      </div>
                      <div className="post-author">
                        <div className="post-author-name">
                          {profile.name}
                          {profile.hasVerifiedBadge && <span style={{ color: '#1877f2' }}>✓</span>}
                        </div>
                        <div className="post-time">
                          {new Date(post.createdAt).toLocaleDateString('en-US', { 
                            month: 'long', 
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="post-content">
                      {post.caption && <p className="post-text">{post.caption}</p>}
                    </div>
                    {post.contentUrl && (
                      post.contentType === 'PHOTO' ? (
                        <img src={post.contentUrl} alt="Post content" className="post-media" />
                      ) : (
                        <video src={post.contentUrl} controls className="post-media" />
                      )
                    )}
                    
                    {/* Post Stats */}
                    <div className="post-stats">
                      <div className="post-stat">
                        <span>❤️</span>
                        <span>{post.likesCount || 0}</span>
                      </div>
                      <div className="post-stat">
                        <span>{post.commentsCount || 0} comments</span>
                      </div>
                    </div>

                    {/* Post Actions */}
                    <div className="post-actions">
                      <button 
                        className="post-action-btn"
                        onClick={() => handleLikeContent(post.id)}
                      >
                        <span>❤️</span>
                        <span>Like</span>
                      </button>
                      <button 
                        className="post-action-btn"
                        onClick={() => handleToggleComments(post.id)}
                      >
                        <span>💬</span>
                        <span>Comment</span>
                      </button>
                    </div>

                    {/* Comments Section */}
                    {expandedComments[post.id] && (
                      <div className="post-comments">
                        {/* Comment Input */}
                        <div className="comment-input-wrapper">
                          <div className="comment-avatar">
                            {profile.photoUrls && profile.photoUrls.length > 0 ? (
                              <img src={profile.photoUrls[0]} alt="You" />
                            ) : (
                              <div style={{ width: '100%', height: '100%', background: '#e4e6eb' }} />
                            )}
                          </div>
                          <div className="comment-input-box">
                            <input
                              type="text"
                              className="comment-input"
                              placeholder="Write a comment..."
                              value={commentText[post.id] || ''}
                              onChange={(e) => setCommentText(prev => ({ ...prev, [post.id]: e.target.value }))}
                              onKeyPress={(e) => e.key === 'Enter' && handleAddComment(post.id)}
                            />
                            <button 
                              className="comment-submit"
                              onClick={() => handleAddComment(post.id)}
                              disabled={!commentText[post.id]?.trim()}
                            >
                              Post
                            </button>
                          </div>
                        </div>

                        {/* Comment List */}
                        {comments[post.id] && comments[post.id].length > 0 && (
                          <div className="comment-list">
                            {comments[post.id].map((comment) => (
                              <div key={comment.id} className="comment-item">
                                <div className="comment-avatar">
                                  {comment.userPhotoUrl ? (
                                    <img src={comment.userPhotoUrl} alt={comment.userName} />
                                  ) : (
                                    <div style={{ width: '100%', height: '100%', background: '#e4e6eb' }} />
                                  )}
                                </div>
                                <div className="comment-content">
                                  <div className="comment-author">{comment.userName}</div>
                                  <p className="comment-text">{comment.commentText}</p>
                                </div>
                                {comment.canDelete && (
                                  <button 
                                    className="comment-delete-btn"
                                    onClick={() => handleDeleteComment(post.id, comment.id)}
                                    title="Delete comment"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" 
                                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* About Tab */}
        {activeTab === 'about' && (
          <div className="about-tab-content">
            <div className="about-section-full">
              <h2>About {profile.name}</h2>
              <div className="about-bio">
                <h3>Bio</h3>
                <p>{profile.creatorBio || profile.bio || 'No bio available'}</p>
              </div>
              
              <div className="about-details">
                <h3>Details</h3>
                <div className="detail-grid">
                  {profile.location && (
                    <div className="detail-item">
                      <span className="detail-icon">📍</span>
                      <div>
                        <div className="detail-label">Location</div>
                        <div className="detail-value">{profile.location}</div>
                      </div>
                    </div>
                  )}
                  {profile.age && (
                    <div className="detail-item">
                      <span className="detail-icon">🎂</span>
                      <div>
                        <div className="detail-label">Age</div>
                        <div className="detail-value">{profile.age} years old</div>
                      </div>
                    </div>
                  )}
                  {profile.gender && (
                    <div className="detail-item">
                      <span className="detail-icon">👤</span>
                      <div>
                        <div className="detail-label">Gender</div>
                        <div className="detail-value">{profile.gender}</div>
                      </div>
                    </div>
                  )}
                  {profile.availableForTravel && (
                    <div className="detail-item">
                      <span className="detail-icon">✈️</span>
                      <div>
                        <div className="detail-label">Travel</div>
                        <div className="detail-value">Available to travel</div>
                      </div>
                    </div>
                  )}
                  {profile.hasParking && (
                    <div className="detail-item">
                      <span className="detail-icon">🅿️</span>
                      <div>
                        <div className="detail-label">Parking</div>
                        <div className="detail-value">Parking available</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {!isOwner && !isSubscribed && (
                <div className="subscription-banner" style={{ marginTop: '24px' }}>
                  <h3>💎 Exclusive Content</h3>
                  <p>Subscribe to access exclusive photos and videos</p>
                  <div className="subscription-price">{profile.subscriptionPrice} credits/month</div>
                  <button className="btn-primary" onClick={handleSubscribe} disabled={subscribing} style={{ width: '100%' }}>
                    {subscribing ? 'Subscribing...' : 'Subscribe Now'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Photos Tab */}
        {activeTab === 'photos' && (
          <div className="photos-tab-content">
            <h2>Photos</h2>
            {!isSubscribed && !isOwner ? (
              <div className="empty-state">
                <div className="empty-state-icon">🔒</div>
                <h3>Subscribe to see photos</h3>
                <p>Subscribe to {profile.name} to access exclusive photos</p>
              </div>
            ) : (
              <div className="photos-grid">
                {content
                  .filter(post => post.contentType === 'PHOTO' && post.contentUrl)
                  .map((post) => (
                    <div key={post.id} className="photo-grid-item">
                      <img src={post.contentUrl} alt={post.caption || 'Photo'} />
                      {post.caption && (
                        <div className="photo-caption">{post.caption}</div>
                      )}
                    </div>
                  ))}
                {content.filter(post => post.contentType === 'PHOTO').length === 0 && (
                  <div className="empty-state">
                    <div className="empty-state-icon">📷</div>
                    <h3>No photos yet</h3>
                    <p>Check back later for new photos</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatorProfile;
